// ═══════════════════════════════════════════════════════
//  CONSULTEC — Database Service
//  src/services/db.js
//  CRUD para todos los módulos. Offline-first con cache.
// ═══════════════════════════════════════════════════════

import { getFirebase }          from './firebase.js'
import { getUser, tienePermiso } from './auth.js'
import { DEMO_DATA }             from '../utils/demoData.js'

// Cache local para modo offline
const localCache = new Map()
const pendingWrites = []
let activeProjectId = null

// ── UTILIDADES ────────────────────────────────────────────
export const setActiveProject = (id) => { activeProjectId = id }
export const getActiveProject = () => activeProjectId

function timestamp () {
  try { return firebase.firestore.FieldValue.serverTimestamp() }
  catch { return new Date().toISOString() }
}

function generateId () {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

// ── WRAPPER GENÉRICO ─────────────────────────────────────
async function fsGet (path) {
  const { db, ready } = getFirebase()
  if (!ready) return localCache.get(path) || null
  const snap = await db.doc(path).get()
  return snap.exists ? { id: snap.id, ...snap.data() } : null
}

async function fsCollection (path, filters = []) {
  const { db, ready } = getFirebase()
  if (!ready) {
    const cached = localCache.get(path) || []
    return Array.isArray(cached) ? cached : [cached]
  }
  let q = db.collection(path)
  filters.forEach(([field, op, val]) => { q = q.where(field, op, val) })
  q = q.orderBy('createdAt', 'desc')
  const snap = await q.get()
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

async function fsSet (path, data) {
  const { db, ready } = getFirebase()
  const withTs = { ...data, updatedAt: timestamp() }
  if (!ready) {
    localCache.set(path, withTs)
    pendingWrites.push({ path, data: withTs })
    return withTs
  }
  await db.doc(path).set(withTs, { merge: true })
  return withTs
}

async function fsAdd (colPath, data) {
  const { db, ready } = getFirebase()
  const withTs = { ...data, createdAt: timestamp(), updatedAt: timestamp() }
  if (!ready) {
    const id = generateId()
    const item = { id, ...withTs }
    const arr = localCache.get(colPath) || []
    localCache.set(colPath, [item, ...arr])
    return item
  }
  const ref = await db.collection(colPath).add(withTs)
  return { id: ref.id, ...withTs }
}

async function fsUpdate (path, data) {
  const { db, ready } = getFirebase()
  const withTs = { ...data, updatedAt: timestamp() }
  if (!ready) {
    const existing = localCache.get(path) || {}
    const updated = { ...existing, ...withTs }
    localCache.set(path, updated)
    return updated
  }
  await db.doc(path).update(withTs)
  return withTs
}

async function fsDelete (path) {
  const { db, ready } = getFirebase()
  if (!ready) { localCache.delete(path); return true }
  await db.doc(path).delete()
  return true
}

// ── PROYECTOS ─────────────────────────────────────────────
export const proyectos = {
  async getAll () {
    const user = getUser()
    if (!user) return DEMO_DATA.proyectos

    if (tienePermiso('canSeeAllProjects')) {
      return fsCollection('projects')
    }
    // Supervisores: solo sus proyectos asignados
    return fsCollection('projects', [['supervisorUid', '==', user.uid]])
  },

  async get (id) {
    return fsGet(`projects/${id}`) || DEMO_DATA.proyectos.find(p => p.id === id)
  },

  async crear (data) {
    const user = getUser()
    return fsAdd('projects', {
      ...data,
      supervisorUid: user?.uid,
      avanceFisico:  0,
      avanceFinanciero: 0,
      status: 'activo'
    })
  },

  async actualizar (id, data) {
    return fsUpdate(`projects/${id}`, data)
  },

  suscribir (id, callback) {
    const { db, ready } = getFirebase()
    if (!ready) { callback(DEMO_DATA.proyectos.find(p => p.id === id)); return () => {} }
    return db.doc(`projects/${id}`).onSnapshot(snap => {
      callback(snap.exists ? { id: snap.id, ...snap.data() } : null)
    })
  }
}

// ── BITÁCORA ──────────────────────────────────────────────
export const bitacora = {
  projectPath: () => `projects/${activeProjectId}/bitacora`,

  async getAll (projectId) {
    const pid = projectId || activeProjectId
    if (!pid) return DEMO_DATA.bitacora
    return fsCollection(`projects/${pid}/bitacora`)
  },

  async agregar (data) {
    const user = getUser()
    const nota = {
      ...data,
      autorUid:    user?.uid || 'demo',
      autorNombre: user?.nombre || 'Demo',
      gps:         data.gps || await obtenerGPS(),
      fotos:       data.fotos || []
    }
    return fsAdd(this.projectPath(), nota)
  },

  async editar (notaId, data) {
    const user = getUser()
    const nota = await fsGet(`${this.projectPath()}/${notaId}`)
    // Solo el autor puede editar (excepto Director)
    if (nota?.autorUid !== user?.uid && !tienePermiso('canReadAll')) {
      throw new Error('No tienes permiso para editar esta nota')
    }
    return fsUpdate(`${this.projectPath()}/${notaId}`, data)
  },

  async eliminar (notaId) {
    const user = getUser()
    const nota = await fsGet(`${this.projectPath()}/${notaId}`)
    if (nota?.autorUid !== user?.uid && !tienePermiso('canApprove')) {
      throw new Error('No tienes permiso para eliminar esta nota')
    }
    return fsDelete(`${this.projectPath()}/${notaId}`)
  },

  suscribir (projectId, callback) {
    const { db, ready } = getFirebase()
    const pid = projectId || activeProjectId
    if (!ready || !pid) { callback(DEMO_DATA.bitacora); return () => {} }
    return db.collection(`projects/${pid}/bitacora`)
      .orderBy('createdAt', 'desc')
      .limit(50)
      .onSnapshot(snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
  }
}

// ── INCIDENCIAS ───────────────────────────────────────────
export const incidencias = {
  projectPath: () => `projects/${activeProjectId}/incidencias`,

  async getAll (projectId) {
    const pid = projectId || activeProjectId
    if (!pid) return DEMO_DATA.incidencias
    return fsCollection(`projects/${pid}/incidencias`)
  },

  async registrar (data) {
    const user = getUser()
    return fsAdd(this.projectPath(), {
      ...data,
      autorUid:    user?.uid || 'demo',
      autorNombre: user?.nombre || 'Demo',
      status:      'abierta',
      fotos:       data.fotos || [],
      gps:         data.gps || await obtenerGPS()
    })
  },

  async actualizar (incId, data) {
    return fsUpdate(`${this.projectPath()}/${incId}`, data)
  },

  async cerrar (incId, resolucion) {
    return fsUpdate(`${this.projectPath()}/${incId}`, {
      status:     'cerrada',
      resolucion,
      closedAt:   timestamp()
    })
  },

  suscribir (projectId, callback) {
    const { db, ready } = getFirebase()
    const pid = projectId || activeProjectId
    if (!ready || !pid) { callback(DEMO_DATA.incidencias); return () => {} }
    return db.collection(`projects/${pid}/incidencias`)
      .where('status', '==', 'abierta')
      .orderBy('createdAt', 'desc')
      .onSnapshot(snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
  }
}

// ── FASES / CRONOGRAMA ────────────────────────────────────
export const fases = {
  projectPath: () => `projects/${activeProjectId}/fases`,

  async getAll (projectId) {
    const pid = projectId || activeProjectId
    if (!pid) return DEMO_DATA.fases
    try {
      const { db, ready } = getFirebase()
      if (!ready) return DEMO_DATA.fases
      const snap = await db.collection(`projects/${pid}/fases`).orderBy('orden').get()
      return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch { return DEMO_DATA.fases }
  },

  async agregar (data) {
    const snap = await this.getAll()
    return fsAdd(this.projectPath(), { ...data, orden: snap.length + 1 })
  },

  async actualizarAvance (faseId, pct) {
    const user = getUser()
    const fase = await fsGet(`${this.projectPath()}/${faseId}`)
    // Verificar que la fase le pertenece al supervisor
    if (fase?.supervisorUid && fase.supervisorUid !== user?.uid && !tienePermiso('canApprove')) {
      throw new Error('Solo puedes actualizar avance de tus fases asignadas')
    }
    return fsUpdate(`${this.projectPath()}/${faseId}`, { pct, status: pct >= 100 ? 'completado' : 'progreso' })
  }
}

// ── PERSONAL ──────────────────────────────────────────────
export const personal = {
  projectPath: () => `projects/${activeProjectId}/personal`,

  async getAll () { return fsCollection(this.projectPath()) || DEMO_DATA.personal },
  async agregar (data) { return fsAdd(this.projectPath(), data) },
  async actualizar (id, data) { return fsUpdate(`${this.projectPath()}/${id}`, data) }
}

// ── DOCUMENTOS ───────────────────────────────────────────
export const documentos = {
  projectPath: () => `projects/${activeProjectId}/docs`,

  async getAll () { return fsCollection(this.projectPath()) || DEMO_DATA.documentos },

  async subir (archivo, metadatos) {
    const { storage, ready } = getFirebase()
    if (!ready) return { ok: false, error: 'Storage no disponible' }

    const path = `projects/${activeProjectId}/docs/${Date.now()}_${archivo.name}`
    const ref  = storage.ref(path)
    const snap = await ref.put(archivo)
    const url  = await snap.ref.getDownloadURL()

    return fsAdd(this.projectPath(), {
      ...metadatos,
      nombre:   archivo.name,
      url,
      size:     archivo.size,
      tipo:     archivo.type,
      storagePath: path
    })
  },

  async eliminar (docId, storagePath) {
    const { storage, ready } = getFirebase()
    if (ready && storagePath) {
      await storage.ref(storagePath).delete().catch(console.warn)
    }
    return fsDelete(`${this.projectPath()}/${docId}`)
  }
}

// ── ESTIMACIONES FINANCIERAS ─────────────────────────────
export const estimaciones = {
  projectPath: () => `projects/${activeProjectId}/estimaciones`,

  async getAll () { return fsCollection(this.projectPath()) || DEMO_DATA.estimaciones },

  async crear (data) {
    if (!tienePermiso('canApprove') && getUser()?.plan !== 'pro') {
      throw new Error('Se requiere Plan Pro para crear estimaciones')
    }
    return fsAdd(this.projectPath(), { ...data, status: 'pendiente' })
  },

  async aprobar (estId, aprobadoPor) {
    if (!tienePermiso('canApprove')) {
      throw new Error('No tienes permiso para aprobar estimaciones')
    }
    return fsUpdate(`${this.projectPath()}/${estId}`, {
      status: 'aprobado', aprobadoPor, aprobadoAt: timestamp()
    })
  }
}

// ── REPORTES ──────────────────────────────────────────────
export const reportes = {
  projectPath: () => `projects/${activeProjectId}/reportes`,

  async getAll () { return fsCollection(this.projectPath()) || [] },

  async crear (tipo, datos) {
    return fsAdd(this.projectPath(), {
      tipo, datos,
      titulo:  generarTituloReporte(tipo),
      firmado: false,
      firmantes: []
    })
  },

  async firmar (repId, firmante) {
    const rep = await fsGet(`${this.projectPath()}/${repId}`)
    const firmantes = [...(rep?.firmantes || []), { ...firmante, fechaFirma: new Date().toISOString() }]
    return fsUpdate(`${this.projectPath()}/${repId}`, { firmantes, firmado: firmantes.length >= 2 })
  }
}

// ── GPS HELPER ────────────────────────────────────────────
export async function obtenerGPS () {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      ()  => resolve(null),
      { timeout: 5000, maximumAge: 60000 }
    )
  })
}

// ── HELPERS ───────────────────────────────────────────────
function generarTituloReporte (tipo) {
  const now  = new Date()
  const sem  = Math.ceil(now.getDate() / 7)
  const mes  = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][now.getMonth()]
  const map  = {
    semanal:    `Reporte Semanal — Semana ${sem} · ${mes} ${now.getFullYear()}`,
    incidencia: `Reporte de Incidencia — ${now.toLocaleDateString('es-MX')}`,
    financiero: `Corte Financiero — ${mes} ${now.getFullYear()}`,
    ejecutivo:  `Informe Ejecutivo — ${mes} ${now.getFullYear()}`,
    cierre:     `Informe de Cierre — ${now.getFullYear()}`
  }
  return map[tipo] || `Reporte — ${now.toLocaleDateString('es-MX')}`
}

// ── SINCRONIZAR ESCRITURAS PENDIENTES ────────────────────
export async function sincronizarPendientes () {
  const { db, ready } = getFirebase()
  if (!ready || pendingWrites.length === 0) return 0

  const batch = db.batch()
  pendingWrites.forEach(({ path, data }) => {
    batch.set(db.doc(path), data, { merge: true })
  })
  await batch.commit()
  const count = pendingWrites.length
  pendingWrites.length = 0
  console.log(`✅ ${count} escrituras pendientes sincronizadas`)
  return count
}

// Escuchar reconexión para sincronizar
window.addEventListener('online', sincronizarPendientes)
